import { useEffect, useState, type ChangeEvent, type FocusEvent } from 'react'
import type { SoapStringField, SoapNumberField, Confidence } from '@yui-receipt/shared'

export type AnySoapField = SoapStringField | SoapNumberField

type Props = {
  label: string
  field: AnySoapField
  fieldPath: string
  type?: 'text' | 'number' | 'textarea'
  placeholder?: string
  disabled?: boolean
  min?: number
  max?: number
  /** Called once per blur if the value changed — parent POSTs /edits. */
  onCommit?: (fieldPath: string, before: string | null, after: string | null) => void
  /** Called when the user taps the ⟵ evidence handle. */
  onEvidenceTap?: (field: AnySoapField, fieldPath: string) => void
}

/**
 * Editable SOAP field with provenance UI:
 *  • confidence underline (dotted amber for medium, filled yellow for low)
 *  • "ต้องตรวจสอบ" chip when confidence is low
 *  • 🔊 button opens the Linked Evidence sheet when transcriptStart exists
 *  • onCommit fires once per blur so the parent can POST /edits
 */
export default function SoapField({
  label,
  field,
  fieldPath,
  type = 'text',
  placeholder,
  disabled,
  min,
  max,
  onCommit,
  onEvidenceTap,
}: Props) {
  const initial = field.value == null ? '' : String(field.value)
  const [draft, setDraft] = useState(initial)

  // Reset local state when upstream value genuinely changes (e.g. draft re-fetch).
  useEffect(() => {
    setDraft(initial)
  }, [initial])

  function handleChange(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setDraft(e.target.value)
  }

  function handleBlur(_: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const before = field.value == null ? null : String(field.value)
    const after = draft.trim() === '' ? null : draft
    if (before === after) return
    onCommit?.(fieldPath, before, after)
  }

  const hasEvidence = typeof field.transcriptStart === 'number' && field.transcriptStart >= 0
  const conf = field.confidence
  const chip = needsReviewChip(field)

  const underline = confidenceUnderlineClass(conf, field.value)
  const inputBase = `input ${underline}`

  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-2">
        <label className="label mb-0" htmlFor={fieldPath}>
          {label}
        </label>
        <div className="flex items-center gap-2">
          {chip}
          {hasEvidence && (
            <button
              type="button"
              onClick={() => onEvidenceTap?.(field, fieldPath)}
              className="text-xs text-brand-700 hover:text-brand-900 underline decoration-dotted min-h-tap px-2"
              aria-label={`ฟังต้นฉบับสำหรับ ${label}`}
            >
              🔊 ฟังต้นฉบับ
            </button>
          )}
        </div>
      </div>

      {type === 'textarea' ? (
        <textarea
          id={fieldPath}
          className={`${inputBase} min-h-[80px]`}
          value={draft}
          placeholder={placeholder}
          disabled={disabled}
          onChange={handleChange}
          onBlur={handleBlur}
          aria-describedby={`${fieldPath}-src`}
        />
      ) : (
        <input
          id={fieldPath}
          className={inputBase}
          type={type}
          inputMode={type === 'number' ? 'decimal' : undefined}
          min={min}
          max={max}
          value={draft}
          placeholder={placeholder}
          disabled={disabled}
          onChange={handleChange}
          onBlur={handleBlur}
          aria-describedby={`${fieldPath}-src`}
        />
      )}

      {/* source phrase + why-null — informational, screen readers get this */}
      <p id={`${fieldPath}-src`} className="text-xs text-slate-500">
        {field.value == null ? (
          <span>
            ไม่พบในเสียง
            {field.missingReason ? ` (${missingReasonLabel(field.missingReason)})` : ''}
          </span>
        ) : field.sourcePhrase ? (
          <span>
            ต้นฉบับ: <span className="italic">"{field.sourcePhrase}"</span>
          </span>
        ) : (
          <span className="opacity-0">·</span>
        )}
      </p>
    </div>
  )
}

function confidenceUnderlineClass(conf: Confidence, value: unknown): string {
  if (value == null || value === '') return ''
  if (conf === 'low') return 'bg-yellow-50 border-yellow-600'
  if (conf === 'medium') return 'border-amber-600 border-b-2 border-dashed'
  return '' // high → no visual marker
}

function needsReviewChip(field: AnySoapField) {
  if (field.confidence === 'low' && field.value != null) {
    return (
      <span className="inline-flex items-center rounded-full bg-yellow-100 text-yellow-900 text-xs px-2 py-0.5">
        ⚠️ ต้องตรวจสอบ
      </span>
    )
  }
  return null
}

function missingReasonLabel(r: string): string {
  switch (r) {
    case 'not_mentioned':
      return 'ไม่ได้พูดถึง'
    case 'unclear':
      return 'ไม่ชัดเจน'
    case 'not_applicable':
      return 'ไม่เกี่ยวข้อง'
    default:
      return r
  }
}
